import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

class AuthService {
  constructor() {
    // Remove any existing interceptors to avoid conflicts
    axios.interceptors.request.handlers = [];
    axios.interceptors.response.handlers = [];
    
    this.setupInterceptors();
  }

  setupInterceptors() {
    // Request interceptor to add token to headers
    axios.interceptors.request.use(
      (config) => {
        // Only add token if no Authorization header is already set
        if (!config.headers.Authorization) {
          const token = this.getAccessToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle token refresh
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const newToken = await this.refreshTokenSilent();
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return axios(originalRequest);
            }
          } catch (refreshError) {
            console.log('Token refresh failed, redirecting to login');
            this.clearTokens();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  async login(email, password) {
    try {
      console.log('Attempting login with:', email);
      const response = await axios.post(`${API}/auth/login`, { email, password });
      console.log('Login response:', response.data);
      
      const { access_token, refresh_token, expires_in, user } = response.data;

      // Store tokens
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('token_expires_at', Date.now() + (expires_in * 1000));
      localStorage.setItem('user', JSON.stringify(user));

      console.log('Tokens stored successfully');
      return { user, access_token };
    } catch (error) {
      console.error('Login error:', error.response?.data || error.message);
      throw error;
    }
  }

  async refreshTokenSilent() {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await axios.post(`${API}/auth/refresh`, {
        refresh_token: refreshToken
      });

      const { access_token, expires_in } = response.data;

      // Update stored access token
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('token_expires_at', Date.now() + (expires_in * 1000));

      return access_token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.clearTokens();
      throw error;
    }
  }

  async logout() {
    const refreshToken = localStorage.getItem('refresh_token');
    
    if (refreshToken) {
      try {
        await axios.post(`${API}/auth/logout`, {
          refresh_token: refreshToken
        });
      } catch (error) {
        // Even if logout request fails, we still clear local tokens
        console.error('Logout request failed:', error);
      }
    }

    this.clearTokens();
  }

  async logoutAll() {
    try {
      await axios.post(`${API}/auth/logout-all`);
    } catch (error) {
      console.error('Logout all request failed:', error);
    }

    this.clearTokens();
  }

  clearTokens() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
    localStorage.removeItem('user');
  }

  getAccessToken() {
    return localStorage.getItem('access_token');
  }

  getRefreshToken() {
    return localStorage.getItem('refresh_token');
  }

  getUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  isTokenExpired() {
    const expiresAt = localStorage.getItem('token_expires_at');
    if (!expiresAt) return true;

    // Check if token expires in the next 5 minutes
    return Date.now() > (parseInt(expiresAt) - 5 * 60 * 1000);
  }

  isAuthenticated() {
    const token = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    return !!(token && refreshToken && !this.isTokenExpired());
  }

  async verifyToken() {
    try {
      const response = await axios.get(`${API}/auth/me`);
      return response.data;
    } catch (error) {
      return null;
    }
  }

  // Auto refresh token if needed
  async ensureValidToken() {
    if (this.isTokenExpired()) {
      try {
        await this.refreshToken();
      } catch (error) {
        throw error;
      }
    }
  }
}

export default new AuthService();